import React, { useState, useEffect, useRef } from "react"
import {
  Container, Row, Col,
  Card, CardHeader, CardTitle, CardBody,
  Button, Badge,
  Modal, ModalHeader, ModalBody,
  FormGroup, Label, Input
} from "reactstrap"
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit'
import paginationFactory from 'react-bootstrap-table2-paginator'
import ReactSelect from 'react-select'
import Swal from "sweetalert2"
import Loading from "lib/loading"
import rest from "lib/rest"
import rotas from "services/rotas"

const CONTROL_HEIGHT = 38

const selectStyles = {
  control:             base => ({ ...base, minHeight: CONTROL_HEIGHT, height: CONTROL_HEIGHT }),
  valueContainer:      base => ({ ...base, height: CONTROL_HEIGHT, padding: '0 12px' }),
  input:               base => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: base => ({ ...base, height: CONTROL_HEIGHT }),
  placeholder:         base => ({ ...base, margin: 0 }),
  singleValue:         base => ({ ...base, margin: 0 }),
}

const ConfigNFe = () => {

  const [loading, setLoading]           = useState(true)
  const [lista, setLista]               = useState([])
  const [listaSelect, setListaSelect]   = useState([])   // opções para o select de filial no modal
  const [modal, setModal]               = useState(false)
  const [filialSel, setFilialSel]       = useState(null) // objeto completo da filial
  const [filialOpt, setFilialOpt]       = useState(null) // opção do ReactSelect (quando abre sem linha)
  const [configAtual, setConfigAtual]   = useState(null)
  const [loadingModal, setLoadingModal] = useState(false)
  const [salvando, setSalvando]         = useState(false)
  const [testando, setTestando]         = useState(false)

  const [tpAmb, setTpAmb]               = useState(2)
  const [certBase64, setCertBase64]     = useState('')
  const [certNome, setCertNome]         = useState('')
  const [senha, setSenha]               = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [subsCert, setSubsCert]         = useState(false)

  const fileRef = useRef(null)
  const { SearchBar } = Search

  useEffect(() => {
    carregarLojas()
    carregarLista()
  }, [])

  // Carrega lista de lojas para o select do modal — independente da nfe_config
  const carregarLojas = () => {
    rest.get(rotas.lojas)
      .then(ret => {
        if (ret.success === 'S' && ret.lista) {
          setListaSelect(ret.lista.map(r => ({
            value:  r.filial_id,
            label:  r.fantasia,
            cnpj:   r.cnpj,
            estado: r.estado,
          })))
        }
      })
      .catch(() => {})
  }

  const carregarLista = () => {
    setLoading(true)
    rest.get(rotas.nfeDfeConfig)
      .then(ret => {
        if (ret.success === 'S') {
          setLista(ret.lista || [])
        } else {
          Swal.fire('Erro', ret.msg || 'Erro ao carregar configurações', 'error')
        }
      })
      .catch(() => Swal.fire('Erro', 'Falha na comunicação com o servidor', 'error'))
      .finally(() => setLoading(false))
  }

  // Abre modal com filial já definida (clique na linha) ou sem (botão Configurar)
  const abrirModal = (filial = null) => {
    setFilialSel(filial)
    setFilialOpt(filial ? { value: filial.filial_id, label: filial.fantasia } : null)
    setConfigAtual(null)
    setCertBase64('')
    setCertNome('')
    setSenha('')
    setSubsCert(false)
    setMostrarSenha(false)
    setModal(true)

    if (filial) carregarConfig(filial.filial_id)
  }

  const carregarConfig = (filial_id) => {
    setLoadingModal(true)
    rest.get(rotas.nfeDfeConfig + '?filial_id=' + filial_id)
      .then(ret => {
        if (ret.success === 'S') {
          setConfigAtual(ret.config)
          setTpAmb(ret.config ? parseInt(ret.config.tp_amb) : 2)
        }
      })
      .finally(() => setLoadingModal(false))
  }

  // Quando seleciona filial no ReactSelect do modal
  const onSelectFilial = (opt) => {
    setFilialOpt(opt)
    if (!opt) { setFilialSel(null); setConfigAtual(null); return }
    // opt já traz cnpj e estado do carregarLojas()
    setFilialSel({ filial_id: opt.value, fantasia: opt.label, cnpj: opt.cnpj, estado: opt.estado })
    setCertBase64('')
    setCertNome('')
    setSenha('')
    setSubsCert(false)
    carregarConfig(opt.value)
  }

  const fecharModal = () => {
    setModal(false)
    setFilialSel(null)
    setFilialOpt(null)
  }

  const handleFile = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pfx') && !file.name.toLowerCase().endsWith('.p12')) {
      Swal.fire('Atenção', 'Selecione um arquivo .pfx ou .p12', 'warning')
      fileRef.current.value = ''
      return
    }
    setCertNome(file.name)
    const reader = new FileReader()
    reader.onload = () => setCertBase64(reader.result.split(',')[1])
    reader.readAsDataURL(file)
  }

  const cancelarSubstituicao = () => {
    setSubsCert(false); setCertBase64(''); setCertNome(''); setSenha('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const montarPayload = (acao, extra = {}) => ({
    acao,
    filial_id:              filialSel.filial_id,
    tp_amb:                 tpAmb,
    certificado_pfx_base64: certBase64,
    certificado_senha:      senha,
    ...extra,
  })

  const salvar = (forcarCnpj = false) => {
    if (!filialSel) { Swal.fire('Atenção', 'Selecione uma loja', 'warning'); return }
    if (!configAtual && !certBase64) { Swal.fire('Atenção', 'Envie o arquivo .pfx do certificado digital', 'warning'); return }
    if (certBase64 && !senha) { Swal.fire('Atenção', 'Informe a senha do certificado', 'warning'); return }
    setSalvando(true)
    rest.post(rotas.nfeDfeConfig, montarPayload('salvar', forcarCnpj ? { forcar_cnpj: true } : {}))
      .then(ret => {
        if (ret.success === 'S') {
          Swal.fire('Sucesso', ret.msg, 'success')
          fecharModal()
          carregarLista()
        } else if (ret.cnpj_divergente) {
          // CNPJ do certificado é diferente do CNPJ da loja — pede confirmação
          Swal.fire({
            title: 'CNPJ divergente',
            text: ret.msg,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Salvar mesmo assim',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#f0ad4e',
          }).then(result => {
            if (result.isConfirmed) salvar(true)
          })
        } else {
          Swal.fire('Erro', ret.msg || 'Erro ao salvar', 'error')
        }
      })
      .catch(() => Swal.fire('Erro', 'Falha na comunicação com o servidor', 'error'))
      .finally(() => setSalvando(false))
  }

  const testar = () => {
    if (!filialSel) { Swal.fire('Atenção', 'Selecione uma loja', 'warning'); return }
    if (!configAtual && !certBase64) { Swal.fire('Atenção', 'Configure o certificado antes de testar', 'warning'); return }
    if (certBase64 && !senha) { Swal.fire('Atenção', 'Informe a senha para testar', 'warning'); return }
    setTestando(true)
    rest.post(rotas.nfeDfeConfig, montarPayload('testar'))
      .then(ret => {
        if (ret.success === 'S') {
          const icon = ret.cnpj_divergente ? 'warning' : 'success'
          const titulo = ret.cnpj_divergente ? 'Certificado válido — CNPJ divergente' : 'Conexão OK'
          Swal.fire(titulo, ret.msg, icon)
        } else {
          Swal.fire('Falha na conexão', ret.msg || 'Verifique o certificado e a senha', 'error')
        }
      })
      .catch(() => Swal.fire('Erro', 'Falha na comunicação com o servidor', 'error'))
      .finally(() => setTestando(false))
  }

  // ── Formatadores das colunas ─────────────────────────────────────────────────

  const fmtData = (iso) => {
    if (!iso) return ''
    const [y, m, d] = iso.split('-')
    return `${d}/${m}/${y}`
  }

  const fmtCnpj = (c) => {
    if (!c) return '—'
    const n = c.replace(/\D/g, '')
    if (n.length !== 14) return c
    return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }

  const fmtCert = (cell, row) => {
    if (!row.tem_certificado || row.tem_certificado === 'N') {
      return <span className="badge badge-warning badge-pill m-0">Não configurado</span>
    }
    if (!row.cert_validade) {
      return <span className="badge badge-success badge-pill m-0">Configurado</span>
    }
    const dias = Math.floor((new Date(row.cert_validade + 'T00:00:00') - new Date()) / 86400000)
    if (dias < 0)  return <span className="badge badge-danger  badge-pill m-0">Vencido</span>
    if (dias < 30) return <span className="badge badge-warning badge-pill m-0">Vence em {dias} dias ({fmtData(row.cert_validade)})</span>
    return <span className="badge badge-success badge-pill m-0">Vigente até {fmtData(row.cert_validade)}</span>
  }

  const fmtAmb = (cell, row) => {
    if (!row.tp_amb) return '—'
    return row.tp_amb == 1
      ? <span className="badge badge-success badge-pill m-0">Produção</span>
      : <span className="badge badge-warning badge-pill m-0">Homologação</span>
  }

  const badgeCertModal = (cfg) => {
    if (!cfg?.tem_certificado || cfg.tem_certificado === 'N') return null
    const dias = cfg.cert_validade
      ? Math.floor((new Date(cfg.cert_validade + 'T00:00:00') - new Date()) / 86400000)
      : null
    if (dias === null) return <span className="badge badge-success badge-pill">Configurado</span>
    if (dias < 0)  return <span className="badge badge-danger  badge-pill">Vencido em {fmtData(cfg.cert_validade)}</span>
    if (dias < 30) return <span className="badge badge-warning badge-pill">Vence em {dias} dias ({fmtData(cfg.cert_validade)})</span>
    return <span className="badge badge-success badge-pill">Vigente até {fmtData(cfg.cert_validade)}</span>
  }

  const colunas = [
    { dataField: 'fantasia', text: 'Loja',
      formatter: (c) => <span style={{ display:'block', textAlign:'left' }}>{c || '—'}</span>,
      headerFormatter: () => <span style={{ display:'block', textAlign:'left' }}>Loja</span> },
    { dataField: 'cnpj',       text: 'CNPJ', formatter: fmtCnpj },
    { dataField: 'estado',     text: 'UF', formatter: (c) => c ? c.toUpperCase() : '—' },
    { dataField: 'tp_amb',     text: 'Ambiente',    formatter: fmtAmb  },
    { dataField: 'tem_cert',   text: 'Certificado', formatter: fmtCert },
  ]

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return <Loading tipo="paginas" />

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="content">
      <Container>
        <Row>
          <Col md="12" className="p-0">
            <Card>
              <CardHeader>
                <CardTitle>CONFIGURAÇÃO NFe — CERTIFICADO DIGITAL</CardTitle>
              </CardHeader>

              <CardBody>
                <Modal isOpen={modal} size="md">
                  <ModalHeader>Configuração NFe</ModalHeader>

                  <FormGroup className="formCadastro">
                    <ModalBody className="pb-0">

                      <FormGroup>
                        <Label>Loja</Label>
                        <ReactSelect
                          placeholder="Selecione a loja..."
                          options={listaSelect}
                          value={filialOpt}
                          onChange={onSelectFilial}
                          styles={selectStyles}
                        />
                      </FormGroup>

                      {filialSel && (
                        <>
                          <Row className="mb-2">
                            <Col xs="6">
                              <small className="text-muted d-block">CNPJ</small>
                              <strong>{fmtCnpj(filialSel.cnpj)}</strong>
                            </Col>
                            <Col xs="6">
                              <small className="text-muted d-block">UF</small>
                              <strong>{filialSel.estado?.toUpperCase()}</strong>
                            </Col>
                          </Row>

                          <hr />

                          {loadingModal ? <Loading tipo="componente" /> : (
                            <>
                              <FormGroup>
                                <Label>Ambiente SEFAZ</Label>
                                <div>
                                  <FormGroup check inline>
                                    <Label check>
                                      <Input type="radio" name="tpAmb" checked={tpAmb === 2} onChange={() => setTpAmb(2)} />
                                      {' '}Homologação <small className="text-muted">(teste)</small>
                                    </Label>
                                  </FormGroup>
                                  <FormGroup check inline>
                                    <Label check>
                                      <Input type="radio" name="tpAmb" checked={tpAmb === 1} onChange={() => setTpAmb(1)} />
                                      {' '}Produção
                                    </Label>
                                  </FormGroup>
                                </div>
                              </FormGroup>

                              <hr />

                              {configAtual?.tem_certificado === 'S' && !subsCert ? (
                                <FormGroup>
                                  <Label>Certificado Digital</Label>
                                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                    {badgeCertModal(configAtual)}
                                    <Button size="sm" color="link" className="p-0 ml-2" onClick={() => setSubsCert(true)}>
                                      Substituir certificado
                                    </Button>
                                  </div>
                                </FormGroup>
                              ) : (
                                <>
                                  <FormGroup>
                                    <Label>{subsCert ? 'Novo certificado (.pfx)' : 'Certificado Digital (.pfx)'}</Label>
                                    {subsCert && (
                                      <div className="mb-2">
                                        <Button size="sm" color="link" className="p-0 text-secondary" onClick={cancelarSubstituicao}>
                                          <i className="fas fa-arrow-left mr-1" />Cancelar substituição
                                        </Button>
                                      </div>
                                    )}
                                    <input
                                      type="file"
                                      accept=".pfx,.p12"
                                      ref={fileRef}
                                      onChange={handleFile}
                                      style={{ display: 'none' }}
                                    />
                                    <div
                                      onClick={() => fileRef.current && fileRef.current.click()}
                                      style={{
                                        border: '2px dashed #ccc',
                                        borderRadius: 6,
                                        padding: '18px 12px',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        background: certBase64 ? '#f0fff4' : '#fafafa',
                                        borderColor: certBase64 ? '#48bb78' : '#ccc',
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      {certBase64 ? (
                                        <>
                                          <i className="fas fa-check-circle fa-lg text-success mb-1 d-block" />
                                          <span className="text-success" style={{ fontWeight: 600 }}>{certNome}</span>
                                          <br />
                                          <small className="text-muted">Clique para trocar o arquivo</small>
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-upload fa-lg text-muted mb-1 d-block" />
                                          <span className="text-muted">Clique para selecionar o arquivo <strong>.pfx</strong></span>
                                        </>
                                      )}
                                    </div>
                                  </FormGroup>

                                  <FormGroup>
                                    <Label>Senha do certificado</Label>
                                    <div className="d-flex align-items-center">
                                      <Input
                                        type={mostrarSenha ? 'text' : 'password'}
                                        value={senha}
                                        onChange={e => setSenha(e.target.value)}
                                        placeholder={
                                          configAtual?.tem_senha === 'S' && !certBase64
                                            ? 'Deixe em branco para manter a atual'
                                            : 'Senha do arquivo .pfx'
                                        }
                                        autoComplete="new-password"
                                      />
                                      <Button color="link" className="ml-1 px-2" onClick={() => setMostrarSenha(v => !v)}>
                                        <i className={`fas fa-eye${mostrarSenha ? '-slash' : ''}`} />
                                      </Button>
                                    </div>
                                  </FormGroup>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}

                      <Col className="text-center p-3" style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          color="secondary"
                          onClick={testar}
                          disabled={testando || salvando || loadingModal || !filialSel}
                          className="mr-1"
                        >
                          {testando ? <><i className="fas fa-spinner fa-spin" /> Testando...</> : 'Testar conexão'}
                        </Button>
                        <Button
                          color="success"
                          onClick={salvar}
                          disabled={salvando || testando || loadingModal || !filialSel}
                          className="mr-1"
                        >
                          {salvando ? <><i className="fas fa-spinner fa-spin" /> Salvando...</> : 'Salvar'}
                        </Button>
                        <Button
                          color="default"
                          onClick={fecharModal}
                          disabled={salvando}
                        >
                          Fechar
                        </Button>
                      </Col>

                    </ModalBody>
                  </FormGroup>
                </Modal>

                <ToolkitProvider
                  search
                  keyField="filial_id"
                  data={lista}
                  columns={colunas}
                >
                  {props => (
                    <div>
                      <Container>
                        <Row className="align-items-center">
                          <Col md="12">
                            <SearchBar
                              className="form-control m-0 w-100"
                              placeholder="Buscar loja..."
                              style={{ height: CONTROL_HEIGHT }}
                              {...props.searchProps}
                            />
                          </Col>
                        </Row>
                      </Container>

                      <hr />

                      <BootstrapTable
                        striped
                        keyField="filial_id"
                        noDataIndication="Nenhuma loja ativa encontrada"
                        rowStyle={{ cursor: 'pointer' }}
                        pagination={paginationFactory({
                          sizePerPage: 10,
                          hideSizePerPage: true,
                          hidePageListOnlyOnePage: true,
                          showTotal: false,
                        })}
                        rowEvents={{ onDoubleClick: (e, row) => abrirModal(row) }}
                        {...props.baseProps}
                      />
                      <br />
                    </div>
                  )}
                </ToolkitProvider>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ConfigNFe
